import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const REFRESH_THROTTLE_MS = 5 * 60 * 1000; // /auth/refresh at most every 5 minutes
const ABSOLUTE_STORAGE_KEY = "asl_session_started_at";

/**
 * Iter75.5 — Session-timeout guardrails.
 *
 * Two independent enforcement axes:
 *
 *   1. **Idle timeout** — 10 minutes of no activity → auto-logout (Iter81).
 *      A modal warns at `idleMs - warningMs` (default: 7 min in) with
 *      a live countdown. The `Stay logged in` button hits `/auth/refresh`.
 *
 *   2. **Absolute lifetime** — 8 hours from first sign-in, regardless of
 *      activity, forces a re-authentication. We stamp
 *      `asl_session_started_at` in localStorage on first-user-detect,
 *      then a per-second poll checks whether the cap has been reached.
 *      On cap: logout + redirect to `/login?reason=absolute`.
 *
 * Timers are re-armed on every activity burst (throttled at 1s) so we
 * don't smash the event loop with rapid re-scheduling during scroll.
 */
export function useInactivityLogout({
    idleMs = 10 * 60 * 1000,
    warningMs = 3 * 60 * 1000,
    absoluteMs = 8 * 60 * 60 * 1000,
} = {}) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [warning, setWarning] = useState(false);
    const [remainingSec, setRemainingSec] = useState(Math.round(warningMs / 1000));

    const warnTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownRef = useRef(null);
    const absoluteTimerRef = useRef(null);
    const lastRefreshAtRef = useRef(0);
    const lastActivityRef = useRef(Date.now());

    const clearTimers = useCallback(() => {
        if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        warnTimerRef.current = null;
        logoutTimerRef.current = null;
        countdownRef.current = null;
    }, []);

    const clearAbsoluteTimer = useCallback(() => {
        if (absoluteTimerRef.current) clearInterval(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
    }, []);

    const armTimers = useCallback(() => {
        clearTimers();
        warnTimerRef.current = setTimeout(() => {
            setWarning(true);
            setRemainingSec(Math.round(warningMs / 1000));
            countdownRef.current = setInterval(() => {
                setRemainingSec((s) => Math.max(0, s - 1));
            }, 1000);
        }, idleMs - warningMs);
        logoutTimerRef.current = setTimeout(async () => {
            clearTimers();
            try { localStorage.removeItem(ABSOLUTE_STORAGE_KEY); } catch (_e) { /* ignore */ }
            try { await logout(); } catch (_e) { /* ignore */ }
            navigate("/login?reason=inactive", { replace: true });
        }, idleMs);
    }, [idleMs, warningMs, logout, navigate, clearTimers]);

    const resetTimer = useCallback(() => {
        if (!user || user === false) return;
        lastActivityRef.current = Date.now();
        if (warning) return;
        armTimers();
        const now = Date.now();
        if (now - lastRefreshAtRef.current >= REFRESH_THROTTLE_MS) {
            lastRefreshAtRef.current = now;
            api.post("/auth/refresh").catch(() => { /* silent */ });
        }
    }, [user, warning, armTimers]);

    const stayLoggedIn = useCallback(async () => {
        try { await api.post("/auth/refresh"); } catch (_e) { /* ignore */ }
        setWarning(false);
        lastRefreshAtRef.current = Date.now();
        armTimers();
    }, [armTimers]);

    // Activity listeners + initial timer arming.
    useEffect(() => {
        if (!user || user === false) return undefined;
        let throttleHandle = null;
        const handle = () => {
            if (throttleHandle) return;
            throttleHandle = setTimeout(() => {
                throttleHandle = null;
                resetTimer();
            }, 1000);
        };
        ACTIVITY_EVENTS.forEach((ev) => document.addEventListener(ev, handle, { passive: true }));
        armTimers();
        return () => {
            ACTIVITY_EVENTS.forEach((ev) => document.removeEventListener(ev, handle));
            if (throttleHandle) clearTimeout(throttleHandle);
            clearTimers();
        };
    }, [user, resetTimer, armTimers, clearTimers]);

    // Route changes count as activity.
    useEffect(() => {
        resetTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Iter75.5 — Absolute-lifetime enforcement.
    useEffect(() => {
        if (!user || user === false) {
            clearAbsoluteTimer();
            return undefined;
        }
        // Stamp the session-start time on first-user-detect.
        let startedAt = null;
        try {
            const raw = localStorage.getItem(ABSOLUTE_STORAGE_KEY);
            startedAt = raw ? Number(raw) : null;
        } catch (_e) { /* ignore */ }
        if (!startedAt || Number.isNaN(startedAt)) {
            startedAt = Date.now();
            try { localStorage.setItem(ABSOLUTE_STORAGE_KEY, String(startedAt)); } catch (_e) { /* ignore */ }
        }

        const tick = async () => {
            const elapsed = Date.now() - startedAt;
            if (elapsed >= absoluteMs) {
                clearAbsoluteTimer();
                try { localStorage.removeItem(ABSOLUTE_STORAGE_KEY); } catch (_e) { /* ignore */ }
                try { await logout(); } catch (_e) { /* ignore */ }
                navigate("/login?reason=absolute", { replace: true });
            }
        };
        // Immediate check on mount (covers refresh-after-8h).
        tick();
        absoluteTimerRef.current = setInterval(tick, 30 * 1000);
        return clearAbsoluteTimer;
    }, [user, absoluteMs, logout, navigate, clearAbsoluteTimer]);

    return { warning, remainingSec, stayLoggedIn };
}
